import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { portfolioApi, type DecisaoIniciativa, type DecisaoRevisao, type IniciativaConsolidada } from '../../api/portfolio.js';

const DECISOES: DecisaoRevisao[] = ['Manter', 'Acelerar', 'Suspender', 'Cancelar'];

interface PainelRevisaoProps {
  portfolioId: string;
  iniciativas: IniciativaConsolidada[];
  decisoesIniciais?: Record<string, DecisaoRevisao>;
  onFechar: () => void;
}

// SPEC-UX §4.9 — painel de revisão: iniciativas em tabela comparável.
// Decisões da revisão exigem justificativa por linha (R8.5).
export function PainelRevisao({ portfolioId, iniciativas, decisoesIniciais, onFechar }: PainelRevisaoProps) {
  const queryClient = useQueryClient();
  const [decisoes, setDecisoes] = useState<Record<string, DecisaoRevisao>>(decisoesIniciais ?? {});
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});

  const executar = useMutation({
    mutationFn: () => {
      const linhas: DecisaoIniciativa[] = iniciativas
        .filter((ini) => decisoes[ini.id])
        .map((ini) => ({ iniciativaTipo: ini.tipo, iniciativaId: ini.id, decisao: decisoes[ini.id]!, justificativa: justificativas[ini.id] ?? '' }));
      return portfolioApi.executarRevisao(portfolioId, 'ordinaria', linhas);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] });
      onFechar();
    },
  });

  const todasComJustificativa = iniciativas.every((ini) => !decisoes[ini.id] || justificativas[ini.id]?.trim());
  const algumaDecisao = Object.keys(decisoes).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" role="dialog" aria-modal="true">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-borda bg-superficie p-5 shadow-xl">
        <h2 className="text-base font-semibold text-ink">Executar revisão de portfólio</h2>

        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-grafite">
              <th className="pb-2">Iniciativa</th>
              <th className="pb-2">Decisão</th>
              <th className="pb-2">Justificativa</th>
            </tr>
          </thead>
          <tbody>
            {iniciativas.map((ini) => (
              <tr key={`${ini.tipo}_${ini.id}`} className="border-t border-borda">
                <td className="py-2 pr-2 text-ink">
                  {ini.tipo}: {ini.id}
                  <p className="fonte-mono text-xs text-grafite">{ini.estado}</p>
                </td>
                <td className="py-2 pr-2">
                  <select
                    value={decisoes[ini.id] ?? ''}
                    onChange={(e) => setDecisoes((prev) => ({ ...prev, [ini.id]: e.target.value as DecisaoRevisao }))}
                    className="rounded border border-borda bg-base-100 px-2 py-1 text-xs text-ink"
                  >
                    <option value="">—</option>
                    {DECISOES.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2">
                  {decisoes[ini.id] && (
                    <input
                      value={justificativas[ini.id] ?? ''}
                      onChange={(e) => setJustificativas((prev) => ({ ...prev, [ini.id]: e.target.value }))}
                      placeholder="Justificativa"
                      className="w-full rounded border border-borda bg-base-100 px-2 py-1 text-xs text-ink"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="rounded px-3 py-2 text-sm text-grafite hover:bg-borda/40">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!algumaDecisao || !todasComJustificativa || executar.isPending}
            onClick={() => executar.mutate()}
            className="rounded bg-aco px-4 py-2 text-sm font-medium text-white hover:bg-aco-700 disabled:opacity-50"
          >
            {executar.isPending ? 'Executando…' : 'Executar revisão'}
          </button>
        </div>
      </div>
    </div>
  );
}
